import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Loader2, Heart, MessageSquareQuote,
  Compass, ShieldAlert, Sparkles, Lightbulb, MessageCircle, Mail, Share2,
} from "lucide-react";
import WeKitLogo from "@/components/WeKitLogo";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

type ParentContent = {
  headline?: string;
  strengths_in_plain_words?: { title: string; explanation: string }[];
  what_this_means?: string;
  conversation_starters?: string[];
  how_to_support?: { do: string; why: string }[];
  what_to_avoid?: string[];
  career_directions?: { name: string; why_it_fits: string }[];
  next_step_together?: string;
  closing_note?: string;
};

type ParentReportRow = {
  id: string;
  assessment_id: string;
  share_token: string;
  revoked: boolean;
  content: ParentContent;
  created_at: string;
};

function ReportBody({ content, studentLabel = "your child" }: { content: ParentContent; studentLabel?: string }) {
  return (
    <div className="space-y-8">
      {content.headline && (
        <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-accent/5">
          <CardContent className="p-6">
            <Sparkles className="h-5 w-5 text-primary mb-3" />
            <p className="font-display text-xl sm:text-2xl font-bold leading-snug">{content.headline}</p>
          </CardContent>
        </Card>
      )}

      {content.strengths_in_plain_words && content.strengths_in_plain_words.length > 0 && (
        <Card className="border-border/50">
          <CardHeader><CardTitle className="font-display text-lg flex items-center gap-2"><Heart className="h-4 w-4 text-primary" /> Strengths in plain words</CardTitle></CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            {content.strengths_in_plain_words.map((s, i) => (
              <div key={i} className="rounded-lg border border-border/50 bg-muted/30 p-4">
                <p className="font-semibold text-foreground">{s.title}</p>
                <p className="text-sm text-muted-foreground mt-1">{s.explanation}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {content.what_this_means && (
        <Card className="border-border/50">
          <CardHeader><CardTitle className="font-display text-lg flex items-center gap-2"><Lightbulb className="h-4 w-4 text-primary" /> What this means</CardTitle></CardHeader>
          <CardContent><p className="text-sm leading-relaxed text-muted-foreground">{content.what_this_means}</p></CardContent>
        </Card>
      )}

      {content.conversation_starters && content.conversation_starters.length > 0 && (
        <Card className="border-border/50">
          <CardHeader><CardTitle className="font-display text-lg flex items-center gap-2"><MessageSquareQuote className="h-4 w-4 text-primary" /> Conversation starters</CardTitle></CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {content.conversation_starters.map((q, i) => (
                <li key={i} className="rounded-lg border border-border/50 bg-muted/20 p-3 text-sm">"{q}"</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {content.how_to_support && content.how_to_support.length > 0 && (
        <Card className="border-border/50">
          <CardHeader><CardTitle className="font-display text-lg">How to support {studentLabel}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {content.how_to_support.map((h, i) => (
              <div key={i} className="flex gap-3">
                <div className="h-7 w-7 shrink-0 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-semibold">{i + 1}</div>
                <div>
                  <p className="font-semibold text-foreground">{h.do}</p>
                  <p className="text-sm text-muted-foreground">{h.why}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {content.what_to_avoid && content.what_to_avoid.length > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardHeader><CardTitle className="font-display text-lg flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-amber-500" /> Common traps to watch</CardTitle></CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground list-disc pl-5">
              {content.what_to_avoid.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </CardContent>
        </Card>
      )}

      {content.career_directions && content.career_directions.length > 0 && (
        <Card className="border-border/50">
          <CardHeader><CardTitle className="font-display text-lg flex items-center gap-2"><Compass className="h-4 w-4 text-primary" /> Career directions to explore</CardTitle></CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            {content.career_directions.map((c, i) => (
              <div key={i} className="rounded-lg border border-border/50 p-4">
                <Badge variant="outline" className="mb-2">{c.name}</Badge>
                <p className="text-sm text-muted-foreground">{c.why_it_fits}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {content.next_step_together && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="p-5">
            <p className="text-xs uppercase tracking-wide text-primary font-semibold">Try this week</p>
            <p className="mt-1 font-medium">{content.next_step_together}</p>
          </CardContent>
        </Card>
      )}

      {content.closing_note && (
        <Card className="border-border/50">
          <CardContent className="p-6">
            <p className="text-sm italic text-muted-foreground leading-relaxed">{content.closing_note}</p>
            <p className="text-xs text-muted-foreground mt-3">— YOMA, WeKIT™</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function ParentReport() {
  const { user } = useAuth();
  const { token } = useParams<{ token?: string }>();
  const isPublic = !!token;

  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState<ParentReportRow | null>(null);
  const [studentName, setStudentName] = useState("your child");

  // Public share view — keep working for previously-generated shared reports
  useEffect(() => {
    if (!isPublic) {
      setLoading(false);
      return;
    }
    (async () => {
      const { data, error } = await supabase.functions.invoke("public-parent-report", {
        body: { token },
      });
      if (!error && data?.report) {
        setReport(data.report as ParentReportRow);
      }
      setLoading(false);
    })();
  }, [isPublic, token]);

  // Owner view — load student name only (generation is coming soon)
  useEffect(() => {
    if (isPublic || !user) return;
    (async () => {
      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", user.id)
        .maybeSingle();
      if (profile?.full_name) setStudentName(profile.full_name.split(" ")[0]);
    })();
  }, [user, isPublic]);

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  // PUBLIC SHARE VIEW — still renders historic reports
  if (isPublic && report) {
    return (
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
          <div className="container mx-auto flex h-16 items-center justify-between px-4 sm:px-6">
            <Link to="/" className="flex items-center gap-2"><WeKitLogo size="sm" /><span className="font-display text-lg font-bold">WeKIT™</span></Link>
          </div>
        </header>
        <main className="container mx-auto max-w-3xl px-4 sm:px-6 py-8 space-y-6">
          <section>
            <p className="text-sm text-muted-foreground">A note from WeKIT™</p>
            <h1 className="mt-1 font-display text-3xl sm:text-4xl font-bold">
              Understanding <span className="gradient-text">{studentName}</span>
            </h1>
            <p className="mt-2 text-muted-foreground">
              A warm, jargon-free guide to what makes them tick — and how to help them thrive.
            </p>
          </section>
          <ReportBody content={report.content} studentLabel={studentName} />
        </main>
      </div>
    );
  }

  // OWNER VIEW — Coming soon state with WhatsApp / Email share of main report
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="container mx-auto flex h-16 items-center justify-between px-4 sm:px-6">
          <Link to="/dashboard" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Dashboard</Link>
        </div>
      </header>

      <main className="container mx-auto max-w-3xl px-4 sm:px-6 py-12 space-y-8">
        <section>
          <p className="text-sm text-muted-foreground">Parent / Guardian Report</p>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-bold">
            Help your family understand <span className="gradient-text">{studentName}</span>
          </h1>
        </section>

        <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-accent/5">
          <CardHeader>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <CardTitle className="flex items-center gap-2 font-display text-xl">
                <Heart className="h-5 w-5 text-primary" /> Parent / Guardian Report
              </CardTitle>
              <Badge className="rounded-full bg-primary/10 text-primary border-primary/20 gap-1">
                <Sparkles className="h-3 w-3" /> Coming soon
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              YOMA will soon write a warm, jargon-free note for your family — explaining your strengths
              and how to support you, with no scores or labels. We're putting the final touches on it.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              In the meantime, you can share your full Career DNA report directly with your parents or
              guardians on WhatsApp or email — no login required for them.
            </p>
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-display text-lg">
              <Share2 className="h-4 w-4 text-primary" /> Share your report with family
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Open your report and tap <span className="font-medium text-foreground">Share with Family</span>{" "}
              to send via WhatsApp or email.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button asChild className="rounded-full hover-glow">
                <Link to="/report#share">
                  Open my report <Share2 className="ml-1 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full gap-2">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(
                    "I just completed my WeKIT™ Career Clarity 360 assessment. Have a look at my personalised report — would love your thoughts!"
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
              </Button>
              <Button asChild variant="outline" className="rounded-full gap-2">
                <a
                  href={`mailto:?subject=${encodeURIComponent("My WeKIT™ Career DNA Report")}&body=${encodeURIComponent(
                    "Hi,\n\nI just completed my WeKIT™ Career Clarity 360 assessment. I'd love for you to see my personalised report.\n\n— Shared via WeKIT™"
                  )}`}
                >
                  <Mail className="h-4 w-4" /> Email
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
